import CloseIcon from "@mui/icons-material/Close";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import { useMemo, useState } from "react";

import { ORDER_TYPE_ENUM, type OrderType } from "@/constants/enums";
import {
  ORDER_BAR_ID,
  PAGE_PADDING,
  TEXT_STROKE,
} from "@/constants/layout";
import {
  ROUTE_DELIVERY_MENU,
  ROUTE_DINE_IN_MENU,
  ROUTE_TAKEAWAY_MENU,
} from "@/constants/routes";
import { Z_INDEX } from "@/constants/zIndex";
import { useAppSelector } from "@/store";

/**
 * `ex` — the cart's order-status header, §7 of
 * `recovery-source/SPEC_staff-login.md` (`live-chunks/mod-91849.js:334`).
 *
 * It is the FIRST child of the cart drawer's scroll body, and it owns three
 * things: the order title, the order-type tabs, and — for staff only — the
 * overflow menu that switches channel.
 *
 * ## It also owns the close button
 *
 * ```js
 * <CloseButton sx={{ display: { [nk]: "none" } }} id={DU.ORDER_BAR} onClick={t}/>
 * ```
 *
 * `nk` is `lg`, so the control exists only where the cart is a temporary
 * drawer. Landing this component is what let the interim close `IconButton`
 * come off `CartPanel` — that button was a recorded stopgap for exactly this,
 * and there must not be two.
 *
 * ## What is inert here, and why that is the original's own shape
 *
 * The original's cart IS an order once one exists: `cart.data` carries `_id`,
 * `order_number`, `order_status`, `rating` and `updatedAt`, because a member of
 * staff can load a placed order back into the basket and edit it. This tree's
 * cart is a basket and nothing else — there is no load-an-order flow and no
 * `PUT` behind one — so `E = null != a` is permanently false and, exactly as
 * the original computes it, that removes:
 *
 *   * the status chip beside the title (`E && null != s`),
 *   * `"Send SMS For Review"` and `"Take Review"` (`E && null != a && …`),
 *   * `"View Bill"` (same guard).
 *
 * The title correspondingly reads `"Order"` rather than `#{order_number}`.
 * Those branches are NOT built as dead code: they are unreachable by the
 * original's own condition on this state, and building them would mean
 * inventing the order-editing flow they belong to. Recorded here rather than
 * faked.
 *
 * The channel switches are NOT part of that: they are gated on `isStaff` and
 * the tenant's three channel flags alone, so they are live.
 */

/** `eh` */
const Header = styled(Stack)({ padding: `0 ${PAGE_PADDING}px` });

/** `eb` — the rounded well the tabs sit in. */
const TabsWell = styled(Stack)(({ theme }) => ({
  borderRadius: 20,
  overflow: "hidden",
  backgroundColor: theme.palette.background.default,
}));

/** `ef` */
const OrderTypeTabs = styled(Tabs)({ minHeight: "34px", height: "34px" });

/** `em` */
const OrderTypeTab = styled(Tab)(({ theme }) => ({
  minHeight: "28px",
  height: "28px",
  fontWeight: 500,
  paddingBottom: "8px",
  fontSize: "13px",
  color: theme.palette.text.primary,
  textTransform: "none",
}));

/**
 * `eg` — the original passes it as `slotProps.indicator`, which is MUI v6's
 * spelling. This tree is on v5, where the same object is `TabIndicatorProps`.
 */
const INDICATOR_PROPS = { style: { height: 4, borderRadius: 4 } };

interface StaffAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

export interface CartOrderStatusHeaderProps {
  /** `ei` — the cart drawer's one close path. */
  onClose: () => void;
  onOrderTypeChange: (next: OrderType) => void;
}

export default function CartOrderStatusHeader({
  onClose,
  onOrderTypeChange,
}: CartOrderStatusHeaderProps) {
  const orderType = useAppSelector((state) => state.cart.orderType);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const {
    web_enable_delivery: deliveryEnabled,
    web_enable_takeaway: takeawayEnabled,
    web_enable_dine_in: dineInEnabled,
    takeaway_is_pickup: takeawayIsPickup,
  } = useAppSelector((state) => state.settings.data);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const closeMenu = () => setAnchor(null);

  const isDineIn = orderType === ORDER_TYPE_ENUM.DINE_IN;
  const isTakeaway = orderType === ORDER_TYPE_ENUM.TAKE_AWAY;
  const isDelivery = orderType === ORDER_TYPE_ENUM.DELIVERY;

  /**
   * `P` — the switch is a HARD navigation, not a router push. The original does
   * `window.location.replace("https://" + host + route)` after a 10ms delay,
   * because the destination reloads the catalog for the other channel from
   * scratch. The protocol is dropped here: this deployment is reached over
   * both http (LAN) and https, and hardcoding one breaks the other.
   */
  const switchChannel = (route: string) => {
    closeMenu();
    window.setTimeout(() => {
      window.location.replace(`${window.location.origin}${route}`);
    }, 10);
  };

  /** `I` — every entry is staff-only. A customer gets no overflow menu at all. */
  const staffActions = useMemo<StaffAction[]>(() => {
    if (!isStaff) return [];
    const actions: StaffAction[] = [];
    if (isDineIn) {
      if (takeawayEnabled) {
        actions.push({
          label: `Switch to ${takeawayIsPickup ? "Pickup" : "Takeaway"}`,
          icon: <ShoppingBagIcon fontSize="small" />,
          onClick: () => switchChannel(ROUTE_TAKEAWAY_MENU),
        });
      }
      if (deliveryEnabled) {
        actions.push({
          label: "Switch to Delivery",
          icon: <DeliveryDiningIcon fontSize="small" />,
          onClick: () => switchChannel(ROUTE_DELIVERY_MENU),
        });
      }
    } else if (dineInEnabled) {
      actions.push({
        label: "Switch to Dine-In",
        icon: <RestaurantIcon fontSize="small" />,
        onClick: () => switchChannel(ROUTE_DINE_IN_MENU),
      });
    }
    return actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryEnabled,
    dineInEnabled,
    isDineIn,
    isStaff,
    takeawayEnabled,
    takeawayIsPickup,
  ]);

  return (
    <Header
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton
          id={ORDER_BAR_ID}
          title="Close"
          aria-label="Close"
          color="secondary"
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
          sx={{ zIndex: Z_INDEX.BASE, display: { lg: "none" } }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Typography variant="h5">Order</Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <TabsWell>
          {dineInEnabled && isDineIn ? (
            <OrderTypeTabs
              value={orderType}
              onChange={(_event, next: OrderType) => onOrderTypeChange(next)}
              TabIndicatorProps={INDICATOR_PROPS}
            >
              <OrderTypeTab
                label="Dine-In"
                value={ORDER_TYPE_ENUM.DINE_IN}
                sx={isDineIn ? { WebkitTextStroke: TEXT_STROKE } : {}}
              />
            </OrderTypeTabs>
          ) : deliveryEnabled || takeawayEnabled ? (
            <OrderTypeTabs
              value={orderType}
              onChange={(_event, next: OrderType) => onOrderTypeChange(next)}
              TabIndicatorProps={INDICATOR_PROPS}
            >
              {takeawayEnabled && (
                <OrderTypeTab
                  label={takeawayIsPickup ? "Pickup" : "Takeaway"}
                  value={ORDER_TYPE_ENUM.TAKE_AWAY}
                  sx={isTakeaway ? { WebkitTextStroke: TEXT_STROKE } : {}}
                />
              )}
              {deliveryEnabled && (
                <OrderTypeTab
                  label="Delivery"
                  value={ORDER_TYPE_ENUM.DELIVERY}
                  sx={isDelivery ? { WebkitTextStroke: TEXT_STROKE } : {}}
                />
              )}
            </OrderTypeTabs>
          ) : null}
        </TabsWell>

        {staffActions.length > 0 && (
          <>
            <IconButton
              size="small"
              aria-label="Order options"
              onClick={(event) => setAnchor(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
              {staffActions.map((action, index) => (
                <MenuItem
                  key={action.label}
                  divider={index !== staffActions.length - 1}
                  onClick={action.onClick}
                  sx={{ minWidth: 200 }}
                >
                  <ListItemIcon>{action.icon}</ListItemIcon>
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>
    </Header>
  );
}
